import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Plus, X, Building2, Flag, Archive, AlertCircle, Trash2, ClipboardCheck } from "lucide-react";
import { LiveRuns } from "@/components/LiveRuns";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const LIVE = ["Opportunity", "Pursuing", "Waiting", "Submitted", "Won"];
const ARCHIVE = ["Lost", "Passed"];
const STAGE_COLORS = {
  Opportunity: "text-zinc-500 border-zinc-300", Pursuing: "text-blue-600 border-blue-400",
  Waiting: "text-amber-600 border-amber-400", Submitted: "text-cyan-600 border-cyan-400",
  Won: "text-emerald-600 border-emerald-400", Lost: "text-red-600 border-red-400",
  Passed: "text-zinc-400 border-zinc-300",
};

export default function Pipeline() {
  const [deals, setDeals] = useState([]);
  const [tab, setTab] = useState("All");
  const [showArchived, setShowArchived] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showPurge, setShowPurge] = useState(false);
  const [drag, setDrag] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canDelete = user?.role === "admin" || user?.role === "officer";

  const load = () => api.get("/bids").then((r) => setDeals(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const deleteBid = async (dealId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this bid and all of its artifacts (docs, forms, envelopes)? This cannot be undone.")) return;
    try {
      await api.delete(`/bids/${dealId}`);
      setDeals((ds) => ds.filter((d) => d.id !== dealId));
      toast.success("Bid deleted");
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const cols = showArchived ? ARCHIVE : LIVE;
  const scoped = deals.filter((d) => tab === "All" || d.bid_type === tab);
  const byStage = (s) => scoped.filter((d) => (d.pipeline_stage || "Opportunity") === s);

  const move = async (dealId, stage) => {
    setDrag(null);
    if (stage === "Won") return toast.error("Use Mark-Won inside the deal.");
    try {
      await api.post(`/bids/${dealId}/pipeline`, { pipeline_stage: stage });
      setDeals((ds) => ds.map((d) => d.id === dealId ? { ...d, pipeline_stage: stage } : d));
      toast.success(`Moved to ${stage}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
  };

  return (
    <div className="p-5 sm:p-8 overflow-x-hidden" data-testid="pipeline-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-zinc-900">Pipeline</h1>
          <p className="text-sm text-zinc-500 mt-1">Deals auto-advance as Anvil works them. Drag to override.</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button data-testid="clear-all-bids-btn" onClick={() => setShowPurge(true)}
              className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border bg-white text-red-600 border-red-200 hover:border-red-400">
              <Trash2 className="h-4 w-4" /> Clear all bids
            </button>
          )}
          <button data-testid="archived-toggle" onClick={() => setShowArchived((v) => !v)}
            className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border ${showArchived ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>
            <Archive className="h-4 w-4" /> {showArchived ? "Live pipeline" : "Archived"}
          </button>
          <button data-testid="new-deal-btn" onClick={() => setShowNew(true)}
            className="flex items-center gap-2 bg-zinc-900 text-white font-semibold text-sm px-4 py-2.5 rounded-md hover:bg-zinc-800"><Plus className="h-4 w-4" /> New deal</button>
        </div>
      </div>

      <div className="flex gap-1 mb-5 border-b border-zinc-200" data-testid="bid-type-tabs">
        {["All", "Federal", "Commercial"].map((t) => (
          <button key={t} data-testid={`tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === t ? "border-gold-dark text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>{t}</button>
        ))}
      </div>

      <LiveRuns />

      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban">
        {cols.map((s) => (
          <div key={s} data-testid={`col-${s.toLowerCase()}`}
            onDragOver={(e) => e.preventDefault()} onDrop={() => drag && move(drag, s)}
            className="min-w-[264px] w-[264px] shrink-0">
            <div className={`text-xs font-semibold uppercase tracking-wider px-2 py-1.5 mb-2 border-l-2 ${STAGE_COLORS[s] || ""}`}>
              {s} <span className="text-zinc-400">· {byStage(s).length}</span>
            </div>
            <div className="space-y-2 min-h-[60px]">
              {byStage(s).map((deal) => (
                <div key={deal.id} data-testid={`deal-card-${deal.id}`}
                  draggable={!showArchived} onDragStart={() => setDrag(deal.id)}
                  onClick={() => navigate(`/deals/${deal.id}`)}
                  className="w-full text-left bg-white border border-zinc-200 hover:border-gold-dark shadow-sm rounded-lg p-3 transition-colors cursor-pointer">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${deal.bid_type === "Federal" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>{deal.bid_type}</span>
                    <div className="flex items-center gap-1.5 min-w-0">
                      {deal.pending_intake_count > 0 && (
                        <span data-testid={`intake-badge-${deal.id}`}
                          title={`${deal.pending_intake_count} intake form${deal.pending_intake_count > 1 ? "s" : ""} pending — open the deal to fill or flag N/A`}
                          className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 shrink-0">
                          <ClipboardCheck className="h-2.5 w-2.5" /> {deal.pending_intake_count}
                        </span>
                      )}
                      {(deal.ai_degraded || deal.commercial_draft_fallback) && (
                        <span data-testid={`deal-degraded-dot-${deal.id}`}
                          title={deal.ai_degraded
                            ? `AI deliverable degraded${deal.ai_degraded_stage ? ` (Stage ${deal.ai_degraded_stage})` : ""} — open the deal to re-run.`
                            : "Commercial terms fell back to heuristic — open the deal to re-draft with Anvil."}
                          className="h-2 w-2 rounded-full bg-amber-500 shrink-0 ring-2 ring-amber-200" />
                      )}
                      <span className="text-[10px] text-zinc-400 font-mono truncate">{deal.est_code}</span>
                      {canDelete && (
                        <button data-testid={`delete-bid-${deal.id}`} onClick={(e) => deleteBid(deal.id, e)}
                          title="Delete this bid" className="text-zinc-300 hover:text-red-600 shrink-0">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-zinc-900 mt-1.5 line-clamp-2">{deal.title}</div>
                  {s === "Waiting" && deal.waiting_reason && (
                    <div data-testid={`waiting-reason-${deal.id}`} className="mt-2 flex items-start gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-1">
                      <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" /><span className="line-clamp-2">{deal.waiting_reason}</span>
                    </div>
                  )}
                  {showArchived && (deal.pass_reason || deal.lost_reason) && (
                    <div className="mt-2 text-[10px] text-zinc-500 italic line-clamp-2">{deal.pass_reason || deal.lost_reason}</div>
                  )}
                  <div className="flex items-center justify-between mt-2 text-[11px] text-zinc-500">
                    <span className="truncate">{deal.estimator || deal.owner_name}</span>
                    <span className="text-zinc-900 font-semibold">{deal.amount || ""}</span>
                  </div>
                </div>
              ))}
              {byStage(s).length === 0 && <div className="text-[11px] text-zinc-300 px-2 py-3">—</div>}
            </div>
          </div>
        ))}
      </div>

      {showNew && <NewDealModal onClose={() => setShowNew(false)} onCreated={(d) => { setShowNew(false); navigate(`/deals/${d.id}`); }} />}
      {showPurge && <PurgeAllModal onClose={() => setShowPurge(false)} onDone={() => { setShowPurge(false); load(); }} />}
    </div>
  );
}

function PurgeAllModal({ onClose, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/bids/purge-all", { confirm });
      toast.success(`Cleared ${r.data?.deals_removed ?? 0} bid(s) — clean slate.`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Purge failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="w-full max-w-md bg-white border border-red-200 rounded-xl p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()} data-testid="purge-all-modal">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-xl text-red-700 flex items-center gap-2"><Trash2 className="h-5 w-5" /> Clear ALL bids</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-zinc-400 hover:text-zinc-900" /></button>
        </div>
        <p className="text-sm text-zinc-600 mb-2">This permanently deletes <b>every</b> bid and all bid artifacts (documents, forms, envelopes, snapshots, POs, projects). The Data Library, the Brain, and the WD identity are NOT touched.</p>
        <p className="text-[12px] text-zinc-500 mb-3">Type <span className="font-mono font-bold text-red-700">DELETE ALL BIDS</span> to confirm.</p>
        <input data-testid="purge-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          placeholder="DELETE ALL BIDS"
          className="w-full bg-white border border-zinc-300 rounded-md px-3 py-2 text-sm text-zinc-900 focus:border-red-400 outline-none" />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-md border border-zinc-300 text-zinc-600 text-sm">Cancel</button>
          <button data-testid="purge-confirm-btn" disabled={busy || confirm !== "DELETE ALL BIDS"} onClick={run}
            className="flex-1 py-2.5 rounded-md bg-red-600 text-white font-semibold text-sm hover:bg-red-700 disabled:opacity-40">
            {busy ? "Clearing…" : "Clear all bids"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function NewDealModal({ onClose, onCreated, lockType }) {
  const [f, setF] = useState({ title: "", bid_type: lockType || "Federal", project_name_abbr: "", state: "",
    solicitation_number: "", agency: "", place_of_performance: "", due_date: "", naics: "", site_code: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.title) return toast.error("Project title is required");
    setBusy(true);
    try {
      const r = await api.post("/bids", f);
      toast.success(`Deal created — ${r.data.est_code}`);
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create deal"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-zinc-200 rounded-xl p-6 max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()} data-testid="new-deal-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-xl text-zinc-900 flex items-center gap-2"><Building2 className="h-5 w-5 text-zinc-900" /> New deal</h2>
          <button onClick={onClose} data-testid="close-new-deal"><X className="h-5 w-5 text-zinc-400 hover:text-zinc-900" /></button>
        </div>
        <div className="space-y-3">
          {!lockType && (
            <div className="flex gap-2">
              {["Federal", "Commercial"].map((t) => (
                <button key={t} data-testid={`new-type-${t.toLowerCase()}`} onClick={() => setF({ ...f, bid_type: t })}
                  className={`flex-1 py-2 text-sm rounded-md border transition-colors flex items-center justify-center gap-1.5 ${
                    f.bid_type === t ? "border-gold-dark text-zinc-900 bg-gold/10" : "border-zinc-300 text-zinc-500"}`}>
                  <Flag className="h-3.5 w-3.5" /> {t}
                </button>
              ))}
            </div>
          )}
          <Field label="Project title" v={f.title} on={set("title")} testid="field-title" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Project abbr (e.g. BNB)" v={f.project_name_abbr} on={set("project_name_abbr")} testid="field-abbr" />
            <Field label="State (e.g. WA)" v={f.state} on={set("state")} testid="field-state" />
          </div>
          {f.bid_type === "Commercial" && (
            <Field label="Site code (e.g. AMF) — sets WD-YEAR-SITE-### number" v={f.site_code} on={set("site_code")} testid="field-site" />
          )}
          <div className="grid grid-cols-2 gap-3">
            {f.bid_type === "Federal" ? (
              <Field label="Solicitation # (this is the federal proposal number)" v={f.solicitation_number} on={set("solicitation_number")} testid="field-sol" />
            ) : null}
            <Field label="Agency / Client" v={f.agency} on={set("agency")} testid="field-agency" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Place of performance" v={f.place_of_performance} on={set("place_of_performance")} testid="field-pop" />
            <Field label="Due date" type="date" v={f.due_date} on={set("due_date")} testid="field-due" />
          </div>
        </div>
        <button data-testid="submit-new-deal" disabled={busy} onClick={submit}
          className="w-full mt-5 bg-zinc-900 text-white font-semibold py-2.5 rounded-md hover:bg-zinc-800 disabled:opacity-50">
          {busy ? "Creating…" : "Create deal & generate EST code"}
        </button>
      </div>
    </div>
  );
}

const Field = ({ label, v, on, type = "text", testid }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</span>
    <input type={type} value={v} onChange={on} data-testid={testid}
      className="w-full mt-1 bg-white border border-zinc-300 rounded-md px-3 py-2 text-sm text-zinc-900 focus:border-gold-dark outline-none" />
  </label>
);
